import axios from 'axios';
import AppConstants from '../constants/AppConstants';
import FormattedShift from '../models/FormattedShift';
import EnumService from './EnumService';
import { getGeoLocation } from '../utils/common';
import {
  ShiftStartLinfoxReq,
  StopShiftReq,
  StartShiftBreakReq,
  StopShiftBreakReq,
  ShiftPageRes,
  ShiftCommentReq,
  ScheduledShiftReq,
  QuickShiftReq,
  CheckinRes,
  FileParameter,
  ImportFromGoogleSheetReq,
  ImportFromGoogleSheetToUiReq,
  QuickShiftBreakReq,
  ShiftWarningReq,
  ScheduleBulkReq,
  ShiftFleetIoFormSubmissionReq
} from '../proxy/proxy';
import { AxiosCheckinsClient, AxiosShiftsClient } from '../proxy/ProxyClients';

// const api = new Client(AppConstants.BASE_API_URL, AxiosShiftsClient);

const ShiftsService = {
  // Driver
  getMyOngoingShift: () => AxiosShiftsClient.latest().then((resp) => resp),

  // Driver
  startScheduledShift: (
    id: number,
    startOdometerKms: number
  ) =>
    getGeoLocation().then((geoLocation) => {
      const payload: ShiftStartLinfoxReq = {
        startOdometerKms: Number(startOdometerKms)
      };
      return AxiosShiftsClient.shiftStartLinfox(id, payload).then((resp) => resp);
    }),

  // Driver
  startAdhocShift: (shiftInfo: any, acceptedQuestionIds: Array<number>) =>
    getGeoLocation().then((geoLocation) => {
      const payload = {
        ...shiftInfo,
        preCheckAnswers: acceptedQuestionIds?.map((id) => ({
          answer: true,
          preCheckQuestionId: id
        })),
        geoLocation
      };
      return AxiosShiftsClient.scheduleBulk({ scheduledShiftReqs: [payload], validatePotentialBreaches: true, notifyDriver: true }).then((resp) => resp);
    }),

  // Driver
  stopShift: (id: number, data: any) =>
    getGeoLocation().then((geoLocation) => {
      const payload: StopShiftReq = {
        ...data,
        geoLocation
      };
      return AxiosShiftsClient
        .quickStopLinfox(id, payload)
        .then((resp) => new FormattedShift(resp));
    }),

  // Owner/Manager
  stopShiftSmsOnly: (id: number) =>
    AxiosShiftsClient.quickStopLinfoxSmsOnly(id).then((resp) => new FormattedShift(resp)),

  // Driver
  startBreak: (id: number) =>
    getGeoLocation().then((geoLocation) => {
      const payload: StartShiftBreakReq = {
        shiftId: Number(id),
        geoLocation: {
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude
        }
      };
      if (geoLocation !== null) {
        return AxiosShiftsClient.startBreak(payload);
      }
      throw new Error('Unable to retrieve geolocation data.');
    }),

  // Driver
  stopBreak: (id: number) =>
    getGeoLocation().then((geoLocation) => {
      const payload: StopShiftBreakReq = {
        shiftId: Number(id),
        geoLocation: {
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude
        }
      };
      if (geoLocation !== null) {
        return AxiosShiftsClient.stopBreak(payload);
      }
      return null;
    }),

  getAllShifts: () =>
    Promise.all([
      EnumService.getEnums('shiftStatus'),
      AxiosShiftsClient.page2({
        pageNumber: 1,
        pageSize: AppConstants.MAX_PAGE_SIZE,
        dateTimeInterval: {
          // provide values for dateTimeInterval if required
        }
      })
    ]).then((resp) => {
      const shiftStatusTypes = resp[0];
      const result: Array<ShiftPageRes> = resp[1]?.items || [];
      return result.map(
        (shift: any) => new FormattedShift(shift, shiftStatusTypes)
      );
    }),

  getAllShifts2: (startDate: string, endDate: string) =>
    Promise.all([
      EnumService.getEnums('shiftStatus'),
      AxiosShiftsClient.page2({
        pageNumber: 1,
        pageSize: AppConstants.MAX_PAGE_SIZE,
        dateTimeInterval: {
          startTimeUtc: startDate,
          endTimeUtc: endDate
        }
      })
    ]).then((resp) => {
      const shiftStatusTypes = resp[0];
      const result: Array<ShiftPageRes> = resp[1]?.items || [];
      return result.map(
        (shift: any) => new FormattedShift(shift, shiftStatusTypes)
      );
    }),

  getShiftById: (id: number) =>
    AxiosShiftsClient.get(id).then((resp) => new FormattedShift(resp)),

  // Driver
  acceptShift: (id: number) =>
    AxiosShiftsClient.accept(id).then((resp) => new FormattedShift(resp)),

  checkIn: async () => {
    const shift = await ShiftsService.getMyOngoingShift();
    if (shift?.shiftStatus === 2) {
      const geoLocation = await getGeoLocation(); // Ensure you're awaiting the promise
      if (geoLocation !== null) {
        const response = await AxiosShiftsClient.ongoingShiftCheckin({
          shiftId: Number(shift.id),
          geoLocation
        });
        return response;
      }
    }
    return null;
  },

  // Driver
  // rejectShift: (id: number) => AxiosShiftsClient.shiftsRejectPOST(id),

  // Owner / Manager
  notifyShift: (id: number) => AxiosShiftsClient.notify(id),

  pushNotify: (id: number) => AxiosShiftsClient.pushNotify(id),

  notifyDriverReturnTime: (id: number) => AxiosShiftsClient.pushNotifyEstimatedVehicleReturn(id),

  // Owner / Manager
  deleteShift: (id: number) => AxiosShiftsClient.delete([id]),

  // Owner / Manager
  bulkDeleteShifts: (ids: Array<number>) => AxiosShiftsClient.delete(ids),

  // Driver
  addShiftComment: (text: string, id: number, shiftCommentTypeId = 0) =>
    getGeoLocation().then((geoLocation) => {
      const payload: ShiftCommentReq = {
        text,
        shiftId: Number(id),
        shiftCommentTypeId,
        geoLocation: geoLocation || undefined // Convert null to undefined
      };
      return AxiosShiftsClient.shiftComment(payload);
    }),

  // Driver
  markJotFormSubmitted: (id: number) =>
    AxiosShiftsClient.jotformSubmitted(id).then((resp) => new FormattedShift(resp)),

  // Owner / Manager
  updateShift: (id: number, payload: ScheduledShiftReq) =>
    AxiosShiftsClient.update(id, payload),

  // Driver
  submitShift: (id: number, payload: QuickShiftReq) =>
    AxiosShiftsClient.quickStopLinfox(id, payload),

  deleteBreak: (shiftId: number, breakId: number) =>
    AxiosShiftsClient.deleteBreak(shiftId, breakId),

  quickBreak: (shiftId: number, payload: QuickShiftBreakReq) =>
    getGeoLocation().then((geoLocation) => {
      const locationpayload: QuickShiftBreakReq = {
        ...payload,
        startGeoLocation: geoLocation || undefined // Convert null to undefined
      };
      return AxiosShiftsClient.quickBreak(shiftId, locationpayload);
    }),

  // Owner / Manager
  uploadShiftSchedules: (
    file: any,
    startDateUtc: string,
    endDateUtc: string,
    notifyAssignedDrivers: boolean
  ) => {
    const formData = new FormData();
    formData.append('ExcelFile', file);
    formData.append('StartDateUtc', startDateUtc);
    formData.append('EndDateUtc', endDateUtc);
    formData.append('NotifyAssignedDrivers', notifyAssignedDrivers.toString());
    return axios.post(
      `${AppConstants.BASE_API_URL}/api/Shifts/UploadSchedule`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  },

  getAllOngoingShiftCheckIns: () =>
    AxiosCheckinsClient
      .pageRecentCheckins({
        pageNumber: 1,
        pageSize: AppConstants.MAX_PAGE_SIZE
      })
      .then((response) => response?.items ?? [])
      .then((checkIns: Array<CheckinRes>) =>
        checkIns.filter(
          (checkIn: CheckinRes) => checkIn?.shift?.shiftStatus === 2
        )),

  // Driver
  preStart30MinuteCheckin: (id: number, photo: FileParameter | undefined) =>
    getGeoLocation().then((geoLocation) => AxiosShiftsClient
      .shiftPreStart30MinuteCheckin2(id, photo, geoLocation?.latitude, geoLocation?.longitude)
      .then((resp) => new FormattedShift(resp))),

  // Driver
  shiftsShiftPreStartPrecheckAnswerLinfox: (
    answer?: boolean,
    photo?: FileParameter,
    requiresPhoto?: boolean,
    preCheckQuestionId?: number,
    shiftId?: number,
    options: any = {}
  ) =>
    getGeoLocation().then((geoLocation) =>
      AxiosShiftsClient
        .shiftPreStartPrecheckAnswerLinfox(
          shiftId,
          answer,
          photo,
          geoLocation?.latitude,
          geoLocation?.longitude,
          requiresPhoto,
          preCheckQuestionId
          // options
        )
        .then((resp) => new FormattedShift(resp))),

  downloadShiftUploadFile: () => AxiosShiftsClient.downloadShiftUploadFile(),

  shiftsImportFromGoogleSheet: (data: ImportFromGoogleSheetReq) =>
    AxiosShiftsClient.importFromGoogleSheet({
      ...data,
      notifyAssignedDrivers: true
    }),

  importFromGoogleSheetToUi: (data: ImportFromGoogleSheetToUiReq) =>
    AxiosShiftsClient.importFromGoogleSheetToUi({
      ...data,
    }),

  scheduleBulk: (body: ScheduleBulkReq | undefined) =>
    AxiosShiftsClient.scheduleBulk(
      body
    ),

  warnDriver: (body: ShiftWarningReq) =>
    AxiosShiftsClient.warnDriver(
      body
    ),

  acknowledgeWarning: (shiftId: number, warningId: number) =>
    AxiosShiftsClient.acknowledgeWarning({
      shiftId,
      shiftWarningId: warningId
    }),

  associateShiftFleetIoFormSubmission: (shiftFleetIoFormSubmissionReq: ShiftFleetIoFormSubmissionReq) =>
    AxiosShiftsClient.associateShiftFleetIoFormSubmission(shiftFleetIoFormSubmissionReq)
};

export default ShiftsService;
